import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { Router } from '@angular/router';
import { LoginRequest, LoginResponse, User, AuthState } from '../models/auth.model';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly TOKEN_KEY = 'auth_token';
  private readonly USER_KEY = 'auth_user';
  private readonly TOKEN_EXPIRY_KEY = 'token_expiry';

  private authStateSubject = new BehaviorSubject<AuthState>({
    isAuthenticated: this.hasValidToken(),
    user: this.getStoredUser(),
    token: this.getStoredToken()
  });

  public authState$ = this.authStateSubject.asObservable();
  public currentUser$ = this.authStateSubject.pipe(
    tap(state => state.user)
  );

  constructor(
    private http: HttpClient,
    private router: Router
  ) {
    // Check token expiry on service initialization
    this.checkTokenExpiry();
  }

  login(credentials: LoginRequest): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${environment.apiUrl}/auth/login`, credentials)
      .pipe(
        tap(response => {
          this.setSession(response);
        })
      );
  }

  logout(): void {
    this.clearSession();
    this.router.navigate(['/login']);
  }

  isAuthenticated(): boolean {
    return this.hasValidToken();
  }

  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  getCurrentUser(): User | null {
    return this.getStoredUser();
  }

  hasRole(roles: number[]): boolean {
    const user = this.getCurrentUser();
    return user ? roles.includes(user.role) : false;
  }

  private setSession(authResult: LoginResponse): void {
    localStorage.setItem(this.TOKEN_KEY, authResult.token);
    localStorage.setItem(this.USER_KEY, JSON.stringify(authResult.user));
    localStorage.setItem(this.TOKEN_EXPIRY_KEY, authResult.expiresAt);

    this.authStateSubject.next({
      isAuthenticated: true,
      user: authResult.user,
      token: authResult.token
    });
  }

  private clearSession(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    localStorage.removeItem(this.TOKEN_EXPIRY_KEY);

    this.authStateSubject.next({
      isAuthenticated: false,
      user: null,
      token: null
    });
  }

  private hasValidToken(): boolean {
    const token = this.getStoredToken();
    if (!token) {
      return false;
    }

    const expiry = localStorage.getItem(this.TOKEN_EXPIRY_KEY);
    if (!expiry) {
      return false;
    }

    const expiryDate = new Date(expiry);
    const now = new Date();

    return now < expiryDate;
  }

  private checkTokenExpiry(): void {
    if (!this.hasValidToken() && this.getStoredToken()) {
      this.clearSession();
    }
  }

  private getStoredToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  private getStoredUser(): User | null {
    const userJson = localStorage.getItem(this.USER_KEY);
    return userJson ? JSON.parse(userJson) : null;
  }
}
